import React, { Fragment, useState } from "react";
import CommonDialog from "../../common/components/CommonDialog";
import { Box, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import moment from "moment";
import { CalendarEventType, EventStatus, Reminders, Repeats, UserTypeId } from "../../common/const";
import CreateEventDialog from "./CreateEventDialog";
import { BorderColor, Check, Close, DeleteForever } from "@mui/icons-material";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { deleteCalendarEvent, updateCalenderEvent } from "../../api/calendarApi";

const ManageEventDialog = ({
    viewOpen,
    setViewOpen,
    viewData,
    getEvents,
    getAllEvents,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [eventOpen, setEventOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [eventId, setEventId] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            {eventOpen && (
                <CreateEventDialog
                    eventOpen={eventOpen}
                    setEventOpen={setEventOpen}
                    getEvents={getEvents}
                    getAllEvents={getAllEvents}
                    selectedEvent={selectedEvent}
                    setSelectedEvent={setSelectedEvent}
                    setViewOpen={setViewOpen}
                    isEdit
                />
            )}
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to remove {viewData.title} event?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await deleteCalendarEvent(eventId);
                                    if (response.data.success) {
                                        showNotification('Calendar event deleted successfully', 'success');
                                        setDeleteOpen(false);
                                        setViewOpen(false);
                                        setEventId(null);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        getEvents();
                                        if (getAllEvents) {
                                            getAllEvents();
                                        }
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setEventId(null);
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={viewOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box sx={{ float: 'right' }}>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setViewOpen(false);
                                }}
                            />
                        </Box>
                    </Fragment>
                }
                content={
                    <Box mt={1}>
                        <Box>
                            <Box mb={1}>
                                <Typography sx={{ fontWeight: 600, fontSize: 17 }}>{viewData?.event_title}</Typography>
                                <Typography sx={{ fontSize: 13 }} color={theme.palette.background.dark}>{moment.utc(viewData?.event_start_timestamp).format('dddd, MMMM, DD, yyyy')}</Typography>
                            </Box>
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <Box
                                    sx={{
                                        background: viewData.event_category === CalendarEventType.application ? '#f8dff1ff'
                                            : viewData.event_category === CalendarEventType.exam ? '#ecf5f8ff'
                                                : viewData.event_category === CalendarEventType.task ? '#e0efe6ff'
                                                    : viewData.event_category === CalendarEventType.counselling ? '#faf1e4ff'
                                                        : '#eceff5ff',
                                        padding: '3px 15px',
                                        marginBottom: 0.5,
                                        borderRadius: 1,
                                        border: `1px solid ${viewData.event_category === CalendarEventType.application ? '#E328AF'
                                            : viewData.event_category === CalendarEventType.exam ? '#5DCFFF'
                                                : viewData.event_category === CalendarEventType.task ? '#00C950'
                                                    : viewData.event_category === CalendarEventType.counselling ? '#FFAB2D'
                                                        : '#6A7282'}`
                                    }}
                                >
                                    <Typography fontSize={13} color={
                                        viewData.event_category === CalendarEventType.application ? '#E328AF'
                                            : viewData.event_category === CalendarEventType.exam ? '#5DCFFF'
                                                : viewData.event_category === CalendarEventType.task ? '#00C950'
                                                    : viewData.event_category === CalendarEventType.counselling ? '#FFAB2D'
                                                        : '#6A7282'}>{viewData.event_category}</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        background: viewData.priority === 'MEDIUM' ? '#FEF9C2' : viewData.priority === 'HIGH' ? '#FFE2E2' : theme.palette.success.light,
                                        padding: '3px 15px',
                                        marginBottom: 0.5,
                                        borderRadius: 1,
                                        border: `1px solid ${viewData.priority === 'MEDIUM' ? '#894B00' : viewData.priority === 'HIGH' ? 'red' : '#01875F'}`
                                    }}
                                >
                                    <Typography fontSize={13} color={viewData.priority === 'Medium' ? '#894B00' : viewData.priority === 'HIGH' ? 'red' : '#01875F'}>{viewData.priority}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box mt={1}>
                            <Typography fontSize={14} fontWeight={600}>Description</Typography>
                            <Typography fontSize={13} color={theme.palette.background.dark}>{viewData.description}</Typography>
                        </Box>
                        <Box mt={1}>
                            <Typography fontSize={14} fontWeight={600}>Linked College</Typography>
                            <Typography fontSize={13} color={theme.palette.background.dark}>{viewData?.college?.college_name || '-'}</Typography>
                        </Box>
                        <Box
                            display="flex"
                            gap={15}
                            mb={1}
                        >
                            <Box mt={1}>
                                <Typography fontSize={14} fontWeight={600}>Reminder</Typography>
                                <Typography fontSize={13} color={theme.palette.background.dark}>{Reminders.find((a) => a.value === viewData.reminder)?.label || 'None'}</Typography>
                            </Box>
                            <Box mt={1}>
                                <Typography fontSize={14} fontWeight={600}>Repeat</Typography>
                                <Typography fontSize={13} color={theme.palette.background.dark}>{Repeats.find((a) => a.value === viewData.repeat)?.label}</Typography>
                            </Box>
                        </Box>
                        {user?.user_type_id === UserTypeId.Student && (
                            <Box display="flex" gap={1} mt={2}>
                                <CommonButton
                                    label="Mark Complete"
                                    size="small"
                                    background={theme.palette.secondary.main}
                                    color={theme.palette.background.main}
                                    startIcon={<Check />}
                                    fontWeight={700}
                                    width='600px'
                                    marginBottom="0px"
                                    handleClick={async () => {
                                        try {
                                            dispatch(setLoading({
                                                loading: true
                                            }));
                                            const response = await updateCalenderEvent(viewData?.event_id, {
                                                user_id: user?.user_id,
                                                status: EventStatus[3]
                                            });
                                            if (response.data?.event_id) {
                                                showNotification('Calendar event mark completed successfully', 'success');
                                                setViewOpen(false);
                                                dispatch(setLoading({
                                                    loading: false
                                                }));
                                            }
                                        } catch (err) {
                                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                            showNotification(errorMessage, 'error');
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                        }
                                    }}
                                />
                                <CommonButton
                                    label="Edit"
                                    size="small"
                                    background='white'
                                    border={`1px solid ${theme.palette.background.light}`}
                                    startIcon={<BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />}
                                    color={theme.palette.secondary.main}
                                    fontWeight={700}
                                    marginBottom="0px"
                                    handleClick={() => {
                                        setSelectedEvent(viewData);
                                        setEventOpen(true);
                                    }}
                                />
                                <CommonButton
                                    label="Remove"
                                    background="white"
                                    border={`1px solid ${theme.palette.background.light}`}
                                    color="red"
                                    startIcon={<DeleteForever sx={{ color: 'red', fontSize: 20 }} />}
                                    fontWeight={700}
                                    marginBottom="0px"
                                    handleClick={() => {
                                        setEventId(viewData.event_id);
                                        setDeleteOpen(true);
                                    }}
                                />
                            </Box>)}
                    </Box>
                }
            />
        </Fragment>
    )
}

export default ManageEventDialog;